import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DealAlerts = ({ onSubscribe }) => {
  const [email, setEmail] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceThreshold, setPriceThreshold] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const categories = [
    { id: 'cordless', label: 'Cordless Vacuums', icon: 'Zap' },
    { id: 'robot', label: 'Robot Vacuums', icon: 'Bot' },
    { id: 'upright', label: 'Upright Vacuums', icon: 'ArrowUp' },
    { id: 'canister', label: 'Canister Vacuums', icon: 'Circle' },
    { id: 'handheld', label: 'Handheld Vacuums', icon: 'Hand' },
    { id: 'wet-dry', label: 'Wet/Dry Vacuums', icon: 'Droplets' }
  ];

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => 
      prev?.includes(categoryId)
        ? prev?.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSubscribe = (e) => {
    e?.preventDefault();
    if (email && selectedCategories?.length > 0) {
      onSubscribe({
        email,
        categories: selectedCategories,
        priceThreshold: priceThreshold || null
      });
      setIsSubscribed(true);
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  return (
    <div className="bg-gradient-to-br from-primary to-secondary p-6 rounded-xl text-primary-foreground">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-primary-foreground/20 p-3 rounded-full">
              <Icon name="Bell" size={24} className="text-primary-foreground" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-2">Never Miss a Great Deal</h2>
          <p className="text-primary-foreground/80">
            Get instant notifications when your favorite vacuum cleaners go on sale
          </p>
        </div>

        {!isSubscribed ? (
          <form onSubmit={handleSubscribe} className="space-y-6">
            {/* Email Input */}
            <div>
              <Input
                type="email"
                label="Email Address"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                required
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/60"
              />
            </div>

            {/* Categories */}
            <div>
              <label className="block text-sm font-medium mb-3">
                Select Categories (Choose at least one)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {categories?.map((category) => (
                  <button
                    key={category?.id}
                    type="button"
                    onClick={() => handleCategoryToggle(category?.id)}
                    className={`flex items-center space-x-2 p-3 rounded-lg border transition-all duration-200 ${
                      selectedCategories?.includes(category?.id)
                        ? 'bg-primary-foreground text-primary border-primary-foreground'
                        : 'bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/20'
                    }`}
                  >
                    <Icon name={category?.icon} size={16} />
                    <span className="text-sm font-medium">{category?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Price Threshold */}
            <div>
              <Input
                type="number"
                label="Price Alert Threshold (Optional)"
                placeholder="e.g., 300"
                value={priceThreshold}
                onChange={(e) => setPriceThreshold(e?.target?.value)}
                description="Get notified when deals are below this price"
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/60"
              />
            </div>

            {/* Subscribe Button */}
            <Button
              type="submit"
              variant="secondary"
              size="lg"
              fullWidth
              iconName="Bell"
              iconPosition="left"
              disabled={!email || selectedCategories?.length === 0}
              className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold"
            >
              Subscribe to Deal Alerts
            </Button>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-primary-foreground/20">
              <div className="flex items-center space-x-2">
                <Icon name="Zap" size={16} className="text-accent" />
                <span className="text-sm">Instant Notifications</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-accent" />
                <span className="text-sm">No Spam Guarantee</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="X" size={16} className="text-accent" />
                <span className="text-sm">Unsubscribe Anytime</span>
              </div>
            </div>
          </form>
        ) : (
          <div className="text-center py-8">
            <div className="bg-success/20 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Icon name="Check" size={24} className="text-success" />
            </div>
            <h3 className="text-xl font-semibold mb-2">You're All Set!</h3>
            <p className="text-primary-foreground/80 mb-4">
              We'll send you deal alerts for {selectedCategories?.length} categories
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsSubscribed(false)}
              className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30"
            >
              Modify Preferences
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealAlerts;